import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DoctorModal from "./DoctorModal";
import { rehabRepository } from "../../../../../domain/rehab/useRehabDomain";
import { REPORT_STATUS } from "../../../../../domain/rehab/rehabEnums";
import "./ReportsTab.css";

/**
 * Reports tab (Section 22-25) — periodic rehabilitation progress
 * snapshots, DERIVED from the same session data the Sessions tab shows
 * (generated by rehabRepository.generateReportSnapshot /
 * buildInitialReports — never independently hand-authored numbers).
 * Reviewing only changes `reviewStatus`/`reviewerNote` — it never
 * touches the underlying session measurements (Section 24/58: live data
 * vs. historical snapshot).
 */
const ReportsTab = ({ bundle }) => {
  const { t, i18n } = useTranslation();
  const { patient, reports } = bundle;
  const [selectedId, setSelectedId] = useState(null);
  const [reviewerNote, setReviewerNote] = useState("");

  const ordered = [...reports].sort((a, b) => new Date(b.periodEnd) - new Date(a.periodEnd));
  const selected = ordered.find((report) => report.id === selectedId) || null;
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, { month: "short", day: "numeric" });

  const openReport = (report) => {
    setSelectedId(report.id);
    setReviewerNote("");
  };

  const handleGenerate = () => rehabRepository.generateReportSnapshot(patient.id);

  const handleMarkReviewed = () => {
    if (!selected) return;
    rehabRepository.markReportReviewed(selected.id, { reviewerNote });
    setSelectedId(null);
  };

  const flagLabel = (flag) => t(`doctorPatientWorkspace.reports.flags.${flag}`);
  const reviewerNoteText = (report) =>
    report.reviewerNote && report.reviewerNote.startsWith("rehabDomain.") ? t(report.reviewerNote) : report.reviewerNote;

  return (
    <div>
      <div className="rp-toolbar">
        <button type="button" className="dw-btn-secondary" onClick={handleGenerate}>
          {t("doctorPatientWorkspace.reports.generateNew")}
        </button>
      </div>

      {ordered.length === 0 ? (
        <div className="dw-empty-state">{t("doctorPatientWorkspace.reports.empty")}</div>
      ) : (
        <div className="rp-list">
          {ordered.map((report) => (
            <button key={report.id} type="button" className="dw-section rp-card" onClick={() => openReport(report)}>
              <div className="rp-card-top">
                <span className="rp-period">
                  {dateFormatter.format(new Date(report.periodStart))} – {dateFormatter.format(new Date(report.periodEnd))}
                </span>
                <span className={`rp-status rp-status--${report.reviewStatus}`}>
                  {t(`doctorPatientWorkspace.reports.status.${report.reviewStatus}`)}
                </span>
              </div>
              <div className="rp-metrics">
                <span>
                  {t("doctorPatientWorkspace.reports.sessions")}: {report.sessionsCompleted}/{report.sessionsExpected}
                </span>
                <span>
                  {t("doctorPatientWorkspace.reports.adherence")}: {report.adherencePercent !== null ? `${report.adherencePercent}%` : "—"}
                </span>
                {report.painTrend && (
                  <span dir="ltr">
                    {t("doctorPatientWorkspace.reports.pain")}: {report.painTrend.from} → {report.painTrend.to}
                  </span>
                )}
                {report.romTrend && (
                  <span dir="ltr">
                    {t("doctorPatientWorkspace.reports.rom")}: {report.romTrend.from}° → {report.romTrend.to}°
                  </span>
                )}
              </div>
              {report.flags.length > 0 && (
                <div className="rp-flags">
                  {report.flags.map((flag) => (
                    <span key={flag} className="rp-flag">
                      {flagLabel(flag)}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <DoctorModal
        isOpen={!!selected}
        onClose={() => setSelectedId(null)}
        title={t("doctorPatientWorkspace.reports.reportTitle")}
        closeLabel={t("doctorPatientWorkspace.close")}
        footer={
          selected && (
            <>
              <button type="button" className="dw-btn-secondary" onClick={() => setSelectedId(null)}>
                {t("doctorPatientWorkspace.done")}
              </button>
              {selected.reviewStatus !== REPORT_STATUS.REVIEWED && (
                <button type="button" className="dw-btn-primary" onClick={handleMarkReviewed}>
                  {t("doctorPatientWorkspace.reports.markReviewed")}
                </button>
              )}
            </>
          )
        }
      >
        {selected && (
          <div className="rp-detail">
            <div className="st-detail-summary">
              <div>
                <span className="st-detail-label">{t("doctorPatientWorkspace.reports.sessions")}</span>
                <span className="st-detail-value" dir="ltr">
                  {selected.sessionsCompleted}/{selected.sessionsExpected}
                </span>
              </div>
              <div>
                <span className="st-detail-label">{t("doctorPatientWorkspace.reports.adherence")}</span>
                <span className="st-detail-value" dir="ltr">
                  {selected.adherencePercent !== null ? `${selected.adherencePercent}%` : "—"}
                </span>
              </div>
              {selected.painTrend && (
                <div>
                  <span className="st-detail-label">{t("doctorPatientWorkspace.reports.pain")}</span>
                  <span className="st-detail-value" dir="ltr">
                    {selected.painTrend.from} → {selected.painTrend.to}
                  </span>
                </div>
              )}
              {selected.romTrend && (
                <div>
                  <span className="st-detail-label">{t("doctorPatientWorkspace.reports.rom")}</span>
                  <span className="st-detail-value" dir="ltr">
                    {selected.romTrend.from}° → {selected.romTrend.to}°
                  </span>
                </div>
              )}
            </div>

            {selected.flags.length > 0 && (
              <div className="rp-flags rp-flags--detail">
                {selected.flags.map((flag) => (
                  <span key={flag} className="rp-flag">
                    {flagLabel(flag)}
                  </span>
                ))}
              </div>
            )}

            {selected.reviewStatus === REPORT_STATUS.REVIEWED && reviewerNoteText(selected) && (
              <p className="rp-existing-note">{reviewerNoteText(selected)}</p>
            )}

            {selected.reviewStatus !== REPORT_STATUS.REVIEWED && (
              <div className="dw-field">
                <label htmlFor="rp-note">{t("doctorPatientWorkspace.reports.addNote")}</label>
                <textarea id="rp-note" rows={3} value={reviewerNote} onChange={(event) => setReviewerNote(event.target.value)} />
              </div>
            )}
          </div>
        )}
      </DoctorModal>
    </div>
  );
};

export default ReportsTab;
